import * as React from 'react'
import {useEffect} from 'react'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import {useQueryParamState} from '../../../../hooks/routes'
import {getBuildType} from '../../../../selectors'
import type {Branch} from '../../../../services/rest'
import type {BuildTypeId} from '../../../../types'
import {BS} from '../../../../types/BS_types'
import {stringifyBranch} from '../../../../utils/branchNames'
import {UserProperties} from '../../../../utils/userProperties'
import ModeSwitch, {Mode} from '../../../common/ModeSwitch/ModeSwitch'
import type {PermalinksQuery} from '../../../common/Permalinks/__generated__/PermalinksQuery.graphql'
import PendingChangesDropdown from '../../../packages/Changes/ChangesDropdown/PendingChangesDropdown.container'

import BuildTypeBranches from './BuildTypeBranches/BuildTypeBranches.container'
import type {BuildTypeHistoryQuery} from './BuildTypeHistory/__generated__/BuildTypeHistoryQuery.graphql'
import BuildTypeHistory from './BuildTypeHistory/BuildTypeHistory.container'
import {defaultMode, isForbiddenCombo, Modes} from './BuildTypeOverviewTab.modes'

import styles from './BuildTypeOverviewTab.css'

import {useAppSelector} from 'src/hooks/react-redux'

type OwnProps = {
  buildTypeId: BuildTypeId
  branch?: Branch | null | undefined
  withBranches: boolean | undefined
  buildTypeHistoryQuery?: PreloadedQuery<BuildTypeHistoryQuery>
  permalinksQuery?: PreloadedQuery<PermalinksQuery>
}

function BuildTypeOverviewTab({
  branch,
  buildTypeId,
  withBranches,
  buildTypeHistoryQuery,
  permalinksQuery,
}: OwnProps) {
  const projectId = useAppSelector(state => getBuildType(state, buildTypeId)?.projectId)
  const builds = buildTypeHistoryQuery && (
    <BuildTypeHistory
      buildTypeId={buildTypeId}
      pageSize={50}
      updatePeriod={10000}
      branch={branch}
      withCollapsedQueued
      withRunningAndQueued
      mainQuery={buildTypeHistoryQuery}
      permalinksQuery={permalinksQuery}
    />
  )
  const pendingChanges = <PendingChangesDropdown buildTypeId={buildTypeId} branch={branch} />
  const [currentMode, setMode] = useQueryParamState('mode', defaultMode)
  useEffect(() => {
    if (
      currentMode === Modes.BRANCHES &&
      (withBranches === false || isForbiddenCombo(branch, Modes.BRANCHES))
    ) {
      setMode(Modes.BUILDS)
    }
  }, [currentMode, setMode, withBranches, branch])
  return withBranches ? (
    <ModeSwitch
      userProperty={UserProperties.BUILD_TYPE_OVERVIEW_MODE}
      preserveParams={({buildTypeTab}, mode) => ({
        buildTypeTab,
        branch: isForbiddenCombo(branch, mode) ? null : stringifyBranch(branch),
      })}
      onSwitch={mode => {
        if (isForbiddenCombo(branch, mode) && BS?.Branch != null && projectId != null) {
          BS.Branch.saveBranch(projectId, BS.Branch.WILDCARD_NAME)
        }
      }}
      className={styles.toggleContainer}
      headerClassName={styles.header}
      customItems={currentMode === Modes.BUILDS && pendingChanges}
    >
      <Mode
        mode={Modes.BRANCHES}
        label="Branches"
        isForbidden={isForbiddenCombo(branch, Modes.BRANCHES)}
      >
        <BuildTypeBranches buildTypeId={buildTypeId} />
      </Mode>
      <Mode isDefault mode={Modes.BUILDS} label="Builds">
        {builds}
      </Mode>
    </ModeSwitch>
  ) : (
    <>
      <div className={styles.header}>{pendingChanges}</div>
      {builds}
    </>
  )
}

export default BuildTypeOverviewTab
